<template>
  <div>
    <RevContainer class="bg-static-success-low pb-24 pt-40 md:py-72">
      <div class="flex flex-col md:flex-row">
        <div
          class="text-static-default-hi flex max-w-[600px] flex-col md:mr-56"
        >
          <RevIllustration
            :alt="i18n(translations.title)"
            class="block h-[140px] w-[137px] self-center md:mb-18 lg:self-start"
            data-qa="payment-success-gif"
            :height="140"
            src="/img/payment/paymentResultThankYou.svg"
            :width="137"
          />
          <h2 class="body-1">
            {{ i18n(translations.subtitle) }}
          </h2>
          <span class="heading-1 mb-18">
            {{ i18n(translations.title) }}
          </span>
          <ResultVoucherCard
            v-if="payment.voucher"
            :payment-method="payment.method as PaymentMethod"
            :voucher="payment.voucher"
          />
          <div class="body-1 mb-16">
            <FormattedMessage :definition="headerDescriptionDefinition">
              <template #link
                ><RevLink
                  target="_blank"
                  :to="resolveLocalizedRoute({ name: ABOUT_US })"
                  >{{
                    i18n(translations.merchants, {
                      merchantCount: merchantsNames.length,
                    })
                  }}</RevLink
                >
              </template>
              <template #merchants>{{ merchantsNames.join(', ') }}</template>
            </FormattedMessage>
          </div>
        </div>

        <div>
          <RevCard
            v-if="isMobileAppAvailable"
            class="flex items-center p-24 md:p-32"
          >
            <RevIllustration
              :alt="i18n(translations.followOrder)"
              class="hidden min-h-[207px] min-w-[100px] max-w-[100px] md:mr-32 md:block"
              :height="207"
              src="/img/payment/phoneImage.png"
              :width="100"
            />
            <div>
              <span class="body-1-bold mb-4">
                {{ i18n(translations.followOrder) }}
              </span>
              <p class="body-1 mb-16">
                {{ i18n(translations.install) }}
              </p>

              <AppStoreButtons
                campaign="payment_confirmation"
                class="flex items-center justify-stretch"
              />
            </div>
          </RevCard>
        </div>
      </div>
    </RevContainer>

    <RevContainer class="mt-40">
      <span class="heading-3">
        {{ i18n(translations.steps) }}
      </span>
      <div class="grid-standard mt-24 gap-16 md:mt-36">
        <div class="flex md:col-span-4 md:block">
          <IconMail class="mr-12 shrink-0 md:mb-28" />
          <div class="body-2">
            {{ i18n(translations.step1) }}
            <span
              class="body-2-bold overflow-wrap-anywhere break-words"
              data-cs-mask
            >
              {{ clientEmail }}
            </span>
          </div>
        </div>
        <div class="flex md:col-span-4 md:block">
          <IconSearch class="mr-12 shrink-0 md:mb-28" />
          <p class="body-2">
            {{ i18n(translations.step2) }}
          </p>
        </div>
        <div class="flex md:col-span-4 md:block">
          <IconPackage class="mr-12 shrink-0 md:mb-28" />
          <div class="body-2">
            <FormattedMessage :definition="translations.step3">
              <template #account>
                <RevLink
                  target="_blank"
                  :to="resolveLocalizedRoute({ name: DASHBOARD.SELF })"
                >
                  {{ i18n(translations.account) }}
                </RevLink>
              </template>
            </FormattedMessage>
          </div>
        </div>
      </div>
    </RevContainer>

    <div v-for="source in payment.sourcingOrders" :key="source.id">
      <RevContainer>
        <RevDivider class="my-36 md:my-56" />
      </RevContainer>

      <RevContainer>
        <span class="heading-3">
          {{ i18n(translations.sourcingOrderTitle, { title: source.title }) }}
        </span>

        <div v-if="source.wording" class="grid-standard mt-24 gap-16 md:mt-36">
          <div
            v-if="source.wording.shipping"
            class="flex md:col-span-4 md:block"
          >
            <IconBill class="mr-12 shrink-0 md:mb-28" />
            <p class="body-2">
              {{ source.wording.shipping }}
            </p>
          </div>

          <div
            v-if="source.wording.information"
            class="flex md:col-span-4 md:block"
          >
            <IconAvatar class="mr-12 shrink-0 md:mb-28" />
            <p class="body-2">
              {{ source.wording.information }}
            </p>
          </div>

          <div
            v-if="source.wording.payment"
            class="flex md:col-span-4 md:block"
          >
            <IconCreditCard class="mr-12 shrink-0 md:mb-28" />
            <p class="body-2">
              {{ source.wording.payment }}
            </p>
          </div>
        </div>
      </RevContainer>
    </div>
    <RevContainer>
      <RevDivider class="my-36 md:my-56" />
    </RevContainer>
    <RevContainer>
      <span class="heading-3">
        {{ orderContainerTitle }}
      </span>

      <div class="grid-standard mt-24 gap-16 md:mt-32">
        <SummaryCard
          :billing-address="payment.client.billingAddress"
          class="md:col-span-3"
          data-cs-mask
          :date="payment.date"
          :is-post-purchase="isPostPurchase"
          :payment-id="payment.paymentId"
          :payment-method="payment.method"
          :shipping-address="payment.client.shippingAddress"
        />
        <div class="md:col-span-9 md:ml-32">
          <div v-if="!isPostPurchase">
            <div
              v-for="order in payment.orders"
              :key="order.id"
              class="mb-16 last-of-type:mb-24"
            >
              <OrderlineSummaryCard
                v-for="orderline in order.orderlines"
                :key="orderline.id"
                data-qa="payment-order-container"
                :merchant="order.merchant"
                :orderline="orderline"
              />
            </div>
          </div>
          <InsuranceSummaryCard
            v-if="isPostPurchase && payment.postPurchase"
            class="md:col-span-9"
            data-qa="payment-order-container"
            :offer="payment.postPurchase?.insuranceOffer"
            :policy="payment.postPurchase?.insurancePolicy"
            :title="payment.postPurchase?.productTitle"
          />

          <div
            v-if="displayServiceFee"
            class="body-2 mt-24 flex justify-between"
          >
            <ServiceFeeModal />
            <div>
              {{ i18n.price(payment.serviceFee) }}
            </div>
          </div>

          <div class="body-1-bold mt-10 flex justify-between">
            <div>{{ i18n(translations.total) }}</div>
            <div data-qa="payment-total-price">
              {{ totalPrice }}
            </div>
          </div>
        </div>
      </div>
    </RevContainer>

    <RevContainer>
      <RevDivider class="my-36 md:my-56" />
    </RevContainer>

    <RevContainer>
      <span class="heading-3">
        {{ i18n(translations.relatedTitle) }}
      </span>
      <div class="grid-standard mt-24 items-end">
        <GarbageCard
          class="md:col-span-3"
          :order-waste="garbage.orderWaste"
          :total-waste="garbage.totalWaste"
        />
        <RevEditorialCard
          v-for="card in cards"
          :key="i18n(card.title)"
          :alt="i18n(card.alt)"
          class="mt-16 md:col-span-3"
          :content="i18n(card.content)"
          :cta="i18n(card.cta)"
          :image="card.image"
          :title="i18n(card.title)"
          title-tag="h1"
          :to="cardLink(card)"
          @click="() => handleClickCard(card.name)"
        />
      </div>
    </RevContainer>

    <RevContainer class="mt-36 pb-80 md:mt-56 md:pb-72">
      <RecommendationCarousel
        :options="{
          withCrossedPrice: true,
          withStartingFrom: true,
          withGrade: true,
          withAddToCart: false,
        }"
        :recommendation-query="{
          category: 'cousinsUp',
          scope: 'product',
          scopeId: firstOrderlineProductId,
          limit: 4,
        }"
        :title="i18n(translations.relatedProducts)"
      />
    </RevContainer>

    <TrustedShopsForm :payment="payment" />
  </div>
</template>

<script setup lang="ts">
import { useRouteLocationWithLocale } from '#imports'
import { computed, ref } from 'vue'

import type { LinkInternal } from '@backmarket/http-api'
import type {
  PaymentMethod,
  PaymentResult,
} from '@backmarket/http-api/src/api-specs-payment/payment'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useUserStore } from '@backmarket/nuxt-module-oauth/useUserStore'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevCard } from '@ds/components/Card'
import { RevContainer } from '@ds/components/Container'
import { RevDivider } from '@ds/components/Divider'
import { RevEditorialCard } from '@ds/components/EditorialCard'
import { RevIllustration } from '@ds/components/Illustration'
import { RevLink } from '@ds/components/Link'
import { IconAvatar } from '@ds/icons/IconAvatar'
import { IconBill } from '@ds/icons/IconBill'
import { IconCreditCard } from '@ds/icons/IconCreditCard'
import { IconMail } from '@ds/icons/IconMail'
import { IconPackage } from '@ds/icons/IconPackage'
import { IconSearch } from '@ds/icons/IconSearch'

import { type Features } from '~/features'
import { ABOUT_US } from '~/scopes/about-us/routes'
import { CMS } from '~/scopes/cms/routes-names'
import { DASHBOARD } from '~/scopes/dashboard/routes'
import { HELP_CENTER } from '~/scopes/help-center/routes'
import RecommendationCarousel from '~/scopes/recommendation/components/RecommendationCarousel.vue'
import TrustedShopsForm from '~/scopes/trustmark/TrustedShops/TrustedShopsForm.vue'

import ServiceFeeModal from '../ServiceFeeModal.vue'

import GarbageCard from './GarbageCard.vue'
import InsuranceSummaryCard from './InsuranceSummaryCard.vue'
import OrderlineSummaryCard from './OrderlineSummaryCard.vue'
import { CARDS, PAYMENT_RESULT_CARD_TYPES } from './PaymentSuccess.config'
import translations from './PaymentSuccess.translations'
import ResultVoucherCard from './ResultVoucherCard.vue'
import SummaryCard from './SummaryCard.vue'

const i18n = useI18n()
const { features } = useMarketplace<Features>()
const resolveLocalizedRoute = useRouteLocationWithLocale()
const userStore = useUserStore()
const tracking = useTracking()

const props = defineProps<{
  payment: PaymentResult
}>()

const isBuyBackEnabled = ref(features.buyback.enabled)
const isMobileAppAvailable = ref(features.mobileApp.available)
const hasOrder = ref(userStore.user.numberOfOrders > 0)

const isCardEnabled = ({ type }: { type: string }) => {
  switch (type) {
    case PAYMENT_RESULT_CARD_TYPES.BUYBACK:
      return isBuyBackEnabled.value
    case PAYMENT_RESULT_CARD_TYPES.REFERRAL_PROGRAM:
      return hasOrder.value
    case PAYMENT_RESULT_CARD_TYPES.REFERRAL_FAQ:
      return (
        !isEmpty(props.payment.links.godfatherDiscountLearnMore) &&
        !hasOrder.value
      )
    case PAYMENT_RESULT_CARD_TYPES.DEALS:
      return !isEmpty(props.payment.links.bestSalesLandingPage)
    default:
      return true
  }
}

const clientEmail = computed(() => props.payment?.client?.email ?? '')
const merchantsNames = computed(() =>
  (props.payment.orders || []).map((order) => order.merchant.name),
)
const cards = computed(() =>
  CARDS.filter((card) => isCardEnabled(card)).slice(0, 3),
)
const cardLinks = computed(() => ({
  [PAYMENT_RESULT_CARD_TYPES.REFERRAL_PROGRAM]: {
    name: DASHBOARD.KYC.REFER_FRIEND,
  },
  // TODO: @perfect-journey
  // To remove when referral program is open in all countries
  [PAYMENT_RESULT_CARD_TYPES.REFERRAL_FAQ]:
    props.payment.links.godfatherDiscountLearnMore,
  [PAYMENT_RESULT_CARD_TYPES.BUYBACK]: {
    name: CMS.BUYBACK,
    params: {
      pageName: 'home',
    },
  },
  [PAYMENT_RESULT_CARD_TYPES.ABOUT_US]: { name: ABOUT_US },
  [PAYMENT_RESULT_CARD_TYPES.DEALS]: props.payment.links.bestSalesLandingPage,
  [PAYMENT_RESULT_CARD_TYPES.FAQ]: { name: HELP_CENTER.HOME },
}))

const garbage = computed(() => ({
  orderWaste: props.payment.garbage.garbage ?? 0,
  totalWaste: props.payment.garbage.garbageTotal ?? 0,
}))

const firstOrderlineProductId = computed(
  () => props.payment.orders[0]?.orderlines[0]?.product?.id ?? '',
)

const headerDescriptionDefinition = computed(() =>
  props.payment.voucher
    ? translations.voucherDescription
    : translations.description,
)

const isPostPurchase = computed(() => !isEmpty(props.payment.postPurchase))

const totalPrice = computed(() =>
  isPostPurchase.value
    ? i18n.price(props.payment.postPurchase?.insuranceOffer.price ?? '')
    : i18n.price(props.payment.amountAfterDiscount),
)

const displayServiceFee = computed(
  () => props.payment.serviceFee && !isPostPurchase.value,
)

const orderContainerTitle = computed(() =>
  isPostPurchase.value
    ? i18n(translations.yourCoverageTitle)
    : i18n(translations.order),
)

const cardLink = ({ type }: { type: string }) => {
  return resolveLocalizedRoute(cardLinks.value[type] as LinkInternal) || ''
}

const handleClickCard = (name: string) => {
  tracking.trackClick({
    name,
    zone: 'payment_confirmation_page',
  })
}
</script>
